/**
 * Ticket List Screen — shows all RN's ticket conversations.
 * Accessible from the Support tab or direct navigation.
 */

import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useFocusEffect, useRouter } from "expo-router";
import { useCallback, useState } from "react";
import { ActivityIndicator, StyleSheet, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { TicketList } from "@/components/tickets/TicketList";
import { colors, fonts, gradients, shadows, spacing, typography } from "@/lib/theme";
import { fetchRnTickets, type Ticket } from "@/lib/ticket-api";

export default function TicketListScreen() {
    const router = useRouter();
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [loading, setLoading] = useState(true);

    const loadTickets = useCallback(async () => {
        try {
            const result = await fetchRnTickets();
            setTickets(result.data);
        } catch {
            // Silently handle
        } finally {
            setLoading(false);
        }
    }, []);

    useFocusEffect(
        useCallback(() => {
            loadTickets();
        }, [loadTickets])
    );

    return (
        <SafeAreaView style={styles.safe} edges={["top"]}>
            <LinearGradient colors={gradients.surface} style={StyleSheet.absoluteFill} />

            <View style={styles.header}>
                <Text style={typography.h2}>Messages</Text>
                <TouchableOpacity
                    style={styles.newBtn}
                    onPress={() => router.push("/tickets/new")}
                    activeOpacity={0.7}
                >
                    <MaterialCommunityIcons name="plus" size={20} color={colors.surface} />
                </TouchableOpacity>
            </View>

            {loading ? (
                <View style={styles.center}>
                    <ActivityIndicator size="large" color={colors.teal500} />
                </View>
            ) : tickets.length === 0 ? (
                <View style={styles.center}>
                    <MaterialCommunityIcons
                        name="chat-outline"
                        size={48}
                        color={colors.textTertiary}
                    />
                    <Text style={styles.emptyTitle}>No conversations yet</Text>
                    <Text style={styles.emptySubtitle}>Tap + to start a new chat</Text>
                </View>
            ) : (
                <TicketList
                    tickets={tickets}
                    onTicketPress={(ticketId) => router.push(`/tickets/${ticketId}`)}
                />
            )}
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    safe: { flex: 1, backgroundColor: colors.background },
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        paddingHorizontal: spacing.lg,
        paddingVertical: spacing.base
    },
    newBtn: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: colors.teal500,
        alignItems: "center",
        justifyContent: "center",
        ...shadows.sm
    },
    center: { flex: 1, alignItems: "center", justifyContent: "center" },
    emptyTitle: {
        fontFamily: fonts.semiBold,
        fontSize: 16,
        color: colors.textPrimary,
        marginTop: spacing.base
    },
    emptySubtitle: {
        fontFamily: fonts.regular,
        fontSize: 14,
        color: colors.textSecondary,
        marginTop: spacing.xs
    }
});
